/**
 * Diagnostics module
 *
 * Captures lightweight runtime signals (console, JS errors, failed network
 * requests, navigation history, clicks, environment) to help triage bug /
 * crash reports. Designed to be privacy-conscious:
 *   - Never captures input values or form data
 *   - Never captures request/response bodies
 *   - Truncates long strings
 *
 * Works alongside LogRocket (already initialized in main.js) which handles
 * full session replay. This module's snapshot includes the LogRocket
 * session URL so admins can jump straight to the replay.
 */

import LogRocket from 'logrocket';

const MAX_NAV = 30;
const MAX_CONSOLE = 100;
const MAX_NETWORK_ERRORS = 30;
const MAX_JS_ERRORS = 20;
const MAX_CLICKS = 50;

const state = {
    navigations: [],
    consoleEntries: [],
    networkErrors: [],
    jsErrors: [],
    clicks: [],
    sessionStart: Date.now(),
    installed: false,
};

function push(arr, item, max) {
    arr.push(item);
    if (arr.length > max) arr.splice(0, arr.length - max);
}

function safeStringify(arg) {
    try {
        if (arg == null) return String(arg);
        if (typeof arg === 'string') {
            return arg.length > 500 ? arg.slice(0, 500) + '…' : arg;
        }
        if (arg instanceof Error) {
            return `${arg.name}: ${arg.message}`;
        }
        const s = JSON.stringify(arg);
        if (!s) return String(arg);
        return s.length > 500 ? s.slice(0, 500) + '…' : s;
    } catch (_) {
        return '[unserializable]';
    }
}

function hookConsole() {
    ['log', 'info', 'warn', 'error', 'debug'].forEach((level) => {
        const original = console[level];
        if (!original || original.__diagHooked) return;
        const wrapped = function (...args) {
            try {
                push(state.consoleEntries, {
                    t: Date.now(),
                    level,
                    msg: args.map(safeStringify).join(' ').slice(0, 1000),
                }, MAX_CONSOLE);
            } catch (_) { /* swallow */ }
            return original.apply(console, args);
        };
        wrapped.__diagHooked = true;
        console[level] = wrapped;
    });
}

function hookJsErrors() {
    window.addEventListener('error', (e) => {
        push(state.jsErrors, {
            t: Date.now(),
            type: 'error',
            message: (e.message || '').toString().slice(0, 500),
            source: (e.filename || '').toString().slice(0, 300),
            lineno: e.lineno,
            colno: e.colno,
            stack: (e.error && e.error.stack) ? String(e.error.stack).slice(0, 2000) : null,
        }, MAX_JS_ERRORS);
    });
    window.addEventListener('unhandledrejection', (e) => {
        const reason = e.reason || {};
        push(state.jsErrors, {
            t: Date.now(),
            type: 'unhandledrejection',
            message: (reason.message || String(reason) || '').toString().slice(0, 500),
            stack: reason.stack ? String(reason.stack).slice(0, 2000) : null,
        }, MAX_JS_ERRORS);
    });
}

function hookAxios(axios) {
    if (!axios || !axios.interceptors || !axios.interceptors.response) return;
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            try {
                const cfg = (error && error.config) || {};
                push(state.networkErrors, {
                    t: Date.now(),
                    method: (cfg.method || 'get').toString().toUpperCase(),
                    url: (cfg.url || '').toString().slice(0, 300),
                    status: (error.response && error.response.status) || null,
                    message: error.message ? String(error.message).slice(0, 300) : null,
                }, MAX_NETWORK_ERRORS);
            } catch (_) { /* swallow */ }
            return Promise.reject(error);
        }
    );
}

function describeElement(el) {
    if (!el || el.nodeType !== 1) return null;
    const tag = (el.tagName || '').toLowerCase();
    const id = el.id ? `#${el.id}` : '';
    let cls = '';
    if (typeof el.className === 'string' && el.className.trim()) {
        cls = '.' + el.className.trim().split(/\s+/).slice(0, 3).join('.');
    }
    const aria = el.getAttribute ? (el.getAttribute('aria-label') || el.getAttribute('title') || '') : '';
    const text = (el.innerText || el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60);
    return {
        selector: `${tag}${id}${cls}`.slice(0, 120),
        label: (aria || text).slice(0, 80) || null,
    };
}

function hookClicks() {
    document.addEventListener('click', (e) => {
        try {
            let el = e.target;
            // Walk up a few levels to find a meaningful interactive element
            for (let i = 0; i < 4 && el && el !== document.body; i++) {
                const tag = el.tagName && el.tagName.toLowerCase();
                const role = el.getAttribute && el.getAttribute('role');
                if (['button', 'a', 'input', 'select', 'textarea', 'label'].indexOf(tag) !== -1 || role) {
                    break;
                }
                el = el.parentElement;
            }
            const desc = describeElement(el || e.target);
            if (!desc) return;
            push(state.clicks, {
                t: Date.now(),
                selector: desc.selector,
                label: desc.label,
                path: location.pathname,
            }, MAX_CLICKS);
        } catch (_) { /* swallow */ }
    }, true);
}

function hookRouter(router) {
    if (!router || typeof router.afterEach !== 'function') return;
    router.afterEach((to, from) => {
        push(state.navigations, {
            t: Date.now(),
            from: (from && from.fullPath) || null,
            to: (to && to.fullPath) || null,
            name: (to && to.name) || null,
        }, MAX_NAV);
    });
}

function environment() {
    const nav = window.navigator || {};
    const perf = (window.performance && window.performance.memory) || {};
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection || {};
    let tz = null;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) {}
    return {
        userAgent: nav.userAgent,
        language: nav.language,
        platform: nav.platform,
        timezone: tz,
        viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
        screen: window.screen ? { w: window.screen.width, h: window.screen.height } : null,
        online: nav.onLine,
        cookiesEnabled: nav.cookieEnabled,
        connection: conn.effectiveType ? {
            type: conn.effectiveType,
            downlink: conn.downlink,
            rtt: conn.rtt,
            saveData: conn.saveData,
        } : null,
        memory: perf.usedJSHeapSize ? {
            usedMB: Math.round(perf.usedJSHeapSize / 1048576),
            totalMB: Math.round(perf.totalJSHeapSize / 1048576),
            limitMB: Math.round(perf.jsHeapSizeLimit / 1048576),
        } : null,
        appVersion: (window.appConfig && window.appConfig.version)
            || (typeof process !== 'undefined' && process.env && process.env.VUE_APP_VERSION)
            || 'unknown',
        apiBaseUrl: window.API_BASE_URL || null,
        href: location.href,
        pathname: location.pathname,
        referrer: document.referrer || null,
    };
}

function userContext() {
    try {
        const raw = localStorage.getItem('userdata');
        if (!raw) return null;
        const u = JSON.parse(raw);
        return {
            userId: u.userId || u.id || null,
            userName: u.userName || null,
            userEmail: u.userEmail || null,
            organisationId: u.organisationId || u.organisation_id || null,
            organisationName: u.organisationName || u.organisation_name || null,
            userType: u.userType || u.user_type || null,
        };
    } catch (_) {
        return null;
    }
}

function getLogRocketSessionURL() {
    return new Promise((resolve) => {
        try {
            if (!LogRocket || typeof LogRocket.getSessionURL !== 'function') {
                return resolve(null);
            }
            let settled = false;
            const timer = setTimeout(() => {
                if (!settled) { settled = true; resolve(null); }
            }, 2500);
            LogRocket.getSessionURL((sessionURL) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(sessionURL || null);
            });
        } catch (_) {
            resolve(null);
        }
    });
}

const diagnostics = {
    install({ router, axios } = {}) {
        if (state.installed) return;
        state.installed = true;
        try { hookConsole(); } catch (_) {}
        try { hookJsErrors(); } catch (_) {}
        try { hookClicks(); } catch (_) {}
        try { hookRouter(router); } catch (_) {}
        try { hookAxios(axios); } catch (_) {}
    },

    async snapshot() {
        const sessionURL = await getLogRocketSessionURL();
        return {
            capturedAt: new Date().toISOString(),
            sessionStartedAt: new Date(state.sessionStart).toISOString(),
            sessionDurationSec: Math.round((Date.now() - state.sessionStart) / 1000),
            logRocketSessionURL: sessionURL,
            environment: environment(),
            user: userContext(),
            currentRoute: location.pathname + location.search + location.hash,
            recentNavigations: state.navigations.slice(),
            recentClicks: state.clicks.slice(),
            recentConsole: state.consoleEntries.slice(),
            recentNetworkErrors: state.networkErrors.slice(),
            jsErrors: state.jsErrors.slice(),
        };
    },

    /**
     * Tag the current LogRocket session with the issue tracking number, so
     * admins can search for it in LogRocket later.
     */
    tagLogRocket(trackingNumber, extra = {}) {
        try {
            if (LogRocket && typeof LogRocket.track === 'function') {
                LogRocket.track('IssueReport', { trackingNumber, ...extra });
            }
        } catch (_) {}
    },
};

export default diagnostics;
export { diagnostics };
